import { Button, createTheme, MantineProvider, Stack, TextInput, Title } from "@mantine/core";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { ChangeEvent, memo, useEffect, useRef, useState } from "react";

import { Popups } from "@/components/@custom/Popups";
import { Toolbar } from "@/components/@custom/Toolbar";
import { WhatsappWidget } from "@/components/@custom/WhatsappWidget";
import { useLogoutOnCompanyMismatch } from "@/features/app/hooks";
import { GeoCoordinates, useGeoRadius } from "@/hooks/@custom/useGeoRadius";
import { useSession } from "@/hooks/@custom/useSession";
import { Layout } from "@/pages/_layouts";
import { THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED } from "@/pages/App/Expedition/ExpeditionHome/expedTypes";
import { Role } from "@/store";
import { useGetIpQuery } from "@/store/apis/ipify";
import { useGetQueueDriverPositionQuery, useGetSettingsByCompanyQuery, useSaveCheckinMutation } from "@/store/apis/nextcargo";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

const theme = createTheme({
  primaryColor: "primary",
  components: {
    Button: Button.extend({
      defaultProps: {
        size: "lg"
      }
    })
  }
});

export const Route = createFileRoute("/app")({
  component: RouteComponent,
  beforeLoad: requireRoles({ allowed: [Role.Motorista], fallback: "/app/login" }),
  head: () => ({
    meta: [{ title: "Início" }]
  })
});

function RouteComponent() {
  useLogoutOnCompanyMismatch();

  const { driver } = useSession();
  const driverId = driver?.id;
  const driverCompanyId = driver?.company.id;

  const [trigger] = useSaveCheckinMutation();
  const { data: allowedIps } = useGetSettingsByCompanyQuery(
    { id: driverCompanyId! },
    {
      skip: !driverCompanyId,
      selectFromResult: (state) => {
        const data = state.data?.checkinIp.split(",") || [];
        return {
          ...state,
          data: data
        };
      },
      pollingInterval: THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED,
      refetchOnMountOrArgChange: false
    }
  );
  const { data: ip } = useGetIpQuery(undefined, {
    selectFromResult: (state) => {
      return {
        ...state,
        data: state.data?.ip
      };
    },
    pollingInterval: THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED,
    refetchOnMountOrArgChange: false
  });
  const { data: driverQueue } = useGetQueueDriverPositionQuery(
    { id: driverId! },
    {
      skip: !driverId,
      pollingInterval: THREEMINUTES_IN_MILISECONDS_UPDATE_EXPED,
      refetchOnMountOrArgChange: false
    }
  );

  const queueId = driverQueue?.queue.id;

  const lastAutoCheckinKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!driverId || !driverCompanyId || !queueId || !ip) return;

    const isAllowed = allowedIps.includes(ip);
    const key = driverId + ":" + driverCompanyId + ":" + queueId + ":" + ip;

    if (!isAllowed && lastAutoCheckinKeyRef.current !== key) {
      lastAutoCheckinKeyRef.current = key;
      trigger({
        id: driverId,
        status: false,
        isPresent: false,
        company: { id: driverCompanyId },
        queue: { id: queueId }
      });
    }

    if (isAllowed) {
      lastAutoCheckinKeyRef.current = null;
    }
  }, [driverId, driverCompanyId, queueId, ip]);

  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <Toolbar.Default />

      <Popups />
      <WhatsappWidget />
      <Layout.Base slots={{ container: { className: cn("max-md:flex-col! max-md:gap-4!") } }}>
        <Outlet />
        <TempGeoDisplay />
      </Layout.Base>
    </MantineProvider>
  );
}

const TempGeoDisplay = memo(function TempGeoDisplay() {
  const [geo, setGeo] = useState<GeoCoordinates>({ latitude: 0, longitude: 0 });
  const geoData = useGeoRadius({
    watch: true,
    center: geo
  });
  const handleGeoChange = (newGeo: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = newGeo.target;

    if (name !== "latitude" && name !== "longitude") return;
    if (isNaN(parseFloat(value))) return setGeo((prev) => ({ ...prev, [name]: 0 }));

    setGeo((prev) => ({
      ...prev,
      [name]: parseFloat(value)
    }));
  };

  const geoAlreadySet = useRef(false);
  useEffect(() => {
    if (!geoData.location || geoAlreadySet.current) return;
    geoAlreadySet.current = true;

    setGeo({
      latitude: geoData.location.latitude,
      longitude: geoData.location.longitude
    });
  }, [geoData.location?.latitude, geoData.location?.longitude]);

  return (
    <Stack>
      <Title order={3}>Teste de geolocalização</Title>
      <TextInput name="latitude" value={geo?.latitude} onChange={handleGeoChange} placeholder="Latitude" label="Latitude" />
      <TextInput name="longitude" value={geo?.longitude} onChange={handleGeoChange} placeholder="Longitude" label="Longitude" />
      <pre>{JSON.stringify(geoData, null, 2)}</pre>
    </Stack>
  );
});
